import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Button,
  Stack,
  Checkbox,
  ListItemText,
  IconButton,
  Collapse,
  ListSubheader,
  Tooltip,
  Chip,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useAuth } from '../../../contexts/AuthContext';
import { useCategoryValues } from '../../../contexts/AttributesContext';
import { useDashboard } from '../../../contexts/DashboardContext';
import { canonicalSearchText, useIndexedValueSearch } from '../../../hooks/useIndexedValueSearch';
import AppDatePicker from '../../common/AppDatePicker';
import DateDraftScope, { INCOMPLETE_DATES_MESSAGE } from '../../common/DateDraftScope';
import {
  getTypedValueLabel,
  normalizeCategoryType,
  toSelectionOption,
} from '../../../utils/tagValueUtils';
import { isExternalTable } from '../../../utils/tableTypes';
import { invalidDateRangeMessage, isInvertedDateRange } from '../../../utils/functionParameterUtils';

// The menu entry that selects rows holding no value for an attribute. It
// travels as a null in the filter's value list, which the backend compiles to
// `IS NULL` (M203).
export const EMPTY_VALUE = '__decode_empty__';
const EMPTY_LABEL = 'Empty';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const getFilterValueKey = (value) => `${typeof value}:${String(value)}`;

const getInitialFilterValue = (column, isAttribute = false) => {
  const type = normalizeCategoryType(column?.type);
  if (type === 'Date') return { gte: '', lte: '' };
  if (isAttribute) return [];
  if (type === 'Text' && column?.index) return [];
  if (type === 'Yes/No') return '';
  return '';
};

const isActiveFilterValue = (value) => {
  if (Array.isArray(value)) return value.filter((item) => item != null).length > 0;
  if (value && typeof value === 'object') {
    return ['gte', 'lte'].some((key) => String(value[key] || '').trim() !== '');
  }
  if (typeof value === 'boolean') return true;
  if (typeof value === 'number') return !Number.isNaN(value);
  if (value === null || value === undefined) return false;
  return String(value).trim() !== '';
};

function DynamicFilters({
  tableSchema,
  onSubmit,
  loading,
  error
}) {
  const { companyName } = useAuth();
  const {
    filterState,
    setFilterState,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
  } = useDashboard();

  const [attributeExpanded, setAttributeExpanded] = useState(false);
  const [detailExpanded, setDetailExpanded] = useState(false);
  const [searchTerms, setSearchTerms] = useState({});
  const isTimeSeries = tableSchema?.tableType === 'timeSeries';
  // A raw table's columns are just columns — never "details", which names the
  // first-write semantics of a merge table.
  const isRaw = tableSchema?.tableType === 'raw';

  // Half-typed dates (see AppDatePicker): Apply is refused while any exist,
  // Clear is live because there is something to discard, and Clear remounts
  // the controls — a draft survives a value that merely stays ''.
  const dateScopeRef = useRef(null);
  const [hasDateDraft, setHasDateDraft] = useState(false);
  const [dateDraftError, setDateDraftError] = useState('');
  const [clearGeneration, setClearGeneration] = useState(0);
  const handleDateDraftChange = (partial) => {
    setHasDateDraft(partial);
    if (!partial) setDateDraftError('');
  };

  // Separate metadata and attribute columns. Derived from the column definitions by value,
  // not from the schema object: the tables list is live, so the object is replaced on every
  // document write (the backend learning a possible value, say), and the tag-value fetch and
  // filter initialisation below must only re-run when the columns themselves change.
  const columnsSignature = JSON.stringify([
    tableSchema?.attributeColumns || [],
    tableSchema?.tableAttributeColumns || [],
    tableSchema?.detailColumns || [],
    // The row key filters like a detail column (M65). An external table's key
    // is already one of its mapped columns.
    (!isExternalTable(tableSchema) && tableSchema?.identifier?.name)
      ? [{ name: tableSchema.identifier.name, type: tableSchema.identifier.type || 'Text' }]
      : [],
  ]);
  const { attributeColumns, detailColumns } = useMemo(() => {
    const [attribute, tableAttribute, detail, identifier] = JSON.parse(columnsSignature);
    return {
      attributeColumns: [...attribute, ...tableAttribute],
      detailColumns: [...identifier, ...detail],
    };
  }, [columnsSignature]);
  const hasSchema = Boolean(tableSchema);
  const tableSchemaId = tableSchema?.id || null;

  // Reset expansion state when the selected table changes
  useEffect(() => {
    setAttributeExpanded(false);
    setDetailExpanded(false);
  }, [tableSchemaId]);

  // The attribute value lists behind the filter pickers are live (they are held
  // open through the AttributesProvider while this panel shows them): a value
  // added in the Attributes manager is offered here without a reload. They are
  // re-requested only when the columns themselves change (columnsSignature above).
  const attributeColumnNames = useMemo(() => attributeColumns.map((column) => column.name), [attributeColumns]);
  const rawAttributeValues = useCategoryValues(attributeColumnNames);
  const attributeValues = useMemo(() => {
    const next = {};
    attributeColumns.forEach((column) => {
      next[column.name] = (rawAttributeValues[column.name] || [])
        .map((valueDoc) => {
          const option = toSelectionOption({ ...valueDoc, type: column.type }, valueDoc.id);
          return {
            ...option,
            label: getTypedValueLabel(option.value, column.type, option.name),
          };
        })
        .filter((option) => option.value != null);
    });
    return next;
  }, [attributeColumns, rawAttributeValues]);

  // Initialize filter values
  useEffect(() => {
    if (!hasSchema) return;

    setFilterState(prevFilterState => {
      const newFilterState = { ...prevFilterState };
      let hasChanges = false;

      [...attributeColumns, ...detailColumns].forEach(column => {
        if (!prevFilterState.hasOwnProperty(column.name)) {
          newFilterState[column.name] = getInitialFilterValue(column, attributeColumns.includes(column));
          hasChanges = true;
        } else if (Array.isArray(prevFilterState[column.name])) {
          // Clean up any existing null values in arrays
          const cleaned = prevFilterState[column.name].filter(v => v != null);
          if (cleaned.length !== prevFilterState[column.name].length) {
            newFilterState[column.name] = cleaned;
            hasChanges = true;
          }
        }
      });

      return hasChanges ? newFilterState : prevFilterState;
    });
  }, [hasSchema, setFilterState, attributeColumns, detailColumns]);

  const handleFilterChange = (columnName, value, options = {}) => {
    const { multiple = false } = options;
    setFilterState(prev => {
      let newValue = value;
      if (multiple) {
        if (!Array.isArray(value)) {
          newValue = [value];
        }
        newValue = newValue.filter(v => v != null);
      }
      return {
        ...prev,
        [columnName]: multiple ? newValue : value
      };
    });
  };

  const handleSearchTermChange = (columnName, value) => {
    setSearchTerms(prev => ({ ...prev, [columnName]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (dateScopeRef.current?.reveal()) {
      setDateDraftError(INCOMPLETE_DATES_MESSAGE);
      return;
    }
    // A start after its end is a mistake, not an empty window (M41).
    if (isTimeSeries && isInvertedDateRange(startDate, endDate)) {
      setDateDraftError(invalidDateRangeMessage());
      return;
    }
    const invertedRange = [...attributeColumns, ...detailColumns].find((column) => {
      const value = filterState[column.name];
      return value && typeof value === 'object' && !Array.isArray(value)
        && isInvertedDateRange(value.gte, value.lte);
    });
    if (invertedRange) {
      setDateDraftError(invalidDateRangeMessage(invertedRange.name));
      return;
    }
    setDateDraftError('');

    const activeFilters = {};
    const rangeFilters = {};
    [...attributeColumns, ...detailColumns].forEach((column) => {
      const key = column.name;
      const value = filterState[key];
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const range = {};
        if (String(value.gte || '').trim() !== '') range.gte = value.gte;
        if (String(value.lte || '').trim() !== '') range.lte = value.lte;
        if (Object.keys(range).length > 0) rangeFilters[key] = range;
        return;
      }
      if (Array.isArray(value)) {
        // The Empty entry is sent as a null: the rows with no value (M203).
        const filteredArray = value.filter(v => v != null).map((v) => (v === EMPTY_VALUE ? null : v));
        if (filteredArray.length > 0) activeFilters[key] = filteredArray;
      } else if (isActiveFilterValue(value)) {
        activeFilters[key] = value;
      }
    });

    onSubmit(
      isTimeSeries ? { ...activeFilters, startDate, endDate } : activeFilters,
      rangeFilters
    );
  };

  // Anything the user has picked in this rail — a reporting-period bound or any
  // column filter. Gates the Clear button so it is only live when it would do something.
  const hasActiveFilters = hasDateDraft
    || (isTimeSeries && (isActiveFilterValue(startDate) || isActiveFilterValue(endDate)))
    || [...attributeColumns, ...detailColumns].some((column) => isActiveFilterValue(filterState[column.name]));

  // Reset every control to its initial value. Only the form is cleared: the data
  // shown is unchanged until the user presses Apply, exactly as with any other edit.
  const handleClearFilters = () => {
    setFilterState(() => {
      const cleared = {};
      attributeColumns.forEach((column) => { cleared[column.name] = getInitialFilterValue(column, true); });
      detailColumns.forEach((column) => { cleared[column.name] = getInitialFilterValue(column, false); });
      return cleared;
    });
    setSearchTerms({});
    if (isTimeSeries) {
      setStartDate('');
      setEndDate('');
    }
    setClearGeneration((n) => n + 1);
    setDateDraftError('');
  };

  const renderMetadataFilter = (column) => {
    const rawValue = filterState[column.name];
    if (normalizeCategoryType(column.type) === 'Date') {
      const range = rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)
        ? rawValue
        : { gte: '', lte: '' };
      return (
        <Box key={column.name}>
          <Typography variant="caption" color="text.secondary">
            {column.name.replace(/_/g, ' ')}
          </Typography>
          <Stack spacing={1} sx={{ mt: 0.5 }}>
            <AppDatePicker
              fullWidth
              size="medium"
              label="From"
              value={range.gte}
              onChange={(nextDate) => handleFilterChange(column.name, { ...range, gte: nextDate })}
            />
            <AppDatePicker
              fullWidth
              size="medium"
              label="To"
              value={range.lte}
              onChange={(nextDate) => handleFilterChange(column.name, { ...range, lte: nextDate })}
            />
          </Stack>
        </Box>
      );
    }
    const value = Array.isArray(rawValue) ? rawValue.filter(v => v != null) : [];
    const availableValues = attributeValues[column.name] || [];
    const searchTerm = searchTerms[column.name] || '';
    const labelMap = new Map([
      ...availableValues.map((option) => [getFilterValueKey(option.value), option.label]),
      [getFilterValueKey(EMPTY_VALUE), EMPTY_LABEL],
    ]);

    const filteredValues = availableValues.filter((option) =>
      option.label && option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const showEmpty = !searchTerm || EMPTY_LABEL.toLowerCase().includes(searchTerm.toLowerCase());

    return (
      <FormControl fullWidth key={column.name}>
        <InputLabel>{column.name.replace(/_/g, ' ')}</InputLabel>
        <Select
          multiple
          value={value}
          onChange={(e) => {
            handleFilterChange(column.name, e.target.value, { multiple: true });
          }}
          onClose={() => handleSearchTermChange(column.name, '')}
          renderValue={(selected) => selected.map((item) => labelMap.get(getFilterValueKey(item)) || String(item)).join(', ')}
          MenuProps={MenuProps}
        >
          <div onMouseDown={(e) => e.stopPropagation()}>
            <ListSubheader>
              <TextField
                size="small"
                autoFocus
                placeholder="Search..."
                fullWidth
                value={searchTerm}
                onChange={(e) => handleSearchTermChange(column.name, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') {
                    e.stopPropagation();
                  }
                }}
              />
            </ListSubheader>
          </div>
          {showEmpty && (
            <MenuItem key={`${column.name}-${EMPTY_VALUE}`} value={EMPTY_VALUE}>
              <Checkbox checked={value.includes(EMPTY_VALUE)} />
              <ListItemText primary={<em>{EMPTY_LABEL}</em>} secondary="Rows with no value" />
            </MenuItem>
          )}
          {filteredValues.map((option) => (
            <MenuItem key={`${column.name}-${getFilterValueKey(option.value)}`} value={option.value}>
              <Checkbox checked={value.some((selected) => getFilterValueKey(selected) === getFilterValueKey(option.value))} />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
          {filteredValues.length === 0 && !showEmpty && searchTerm && (
            <MenuItem disabled>No matching options</MenuItem>
          )}
        </Select>
      </FormControl>
    );
  };

  const renderAttributeFilter = (column) => {
    const declaredType = normalizeCategoryType(column.type);
    const value = Object.prototype.hasOwnProperty.call(filterState, column.name)
      ? filterState[column.name]
      : getInitialFilterValue(column, false);

    // A Decode table's identifier is always indexed, and its suggestions take the
    // identifier branch of the search (hooks/useIndexedValueSearch.js).
    const isIdentifier = !isExternalTable(tableSchema) && column.name === tableSchema?.identifier?.name;
    const isIndexed = isIdentifier
      || (!isExternalTable(tableSchema) && !!(tableSchema?.detailColumns || []).find(c => c?.name === column.name && c?.index));
    if (isIndexed && declaredType === 'Text') {
      return (
        <IndexedColumnFilter
          key={column.name}
          column={column}
          isIdentifier={isIdentifier}
          tableId={tableSchema?.id || tableSchema?.tableId || tableSchema?.name}
          values={Array.isArray(value) ? value : (typeof value === 'string' && value ? value.split(',').map(v => v.trim()).filter(Boolean) : [])}
          onChange={(arr) => handleFilterChange(column.name, (arr || []).filter(v => v != null), { multiple: true })}
          companyId={companyName}
        />
      );
    }

    switch (declaredType) {
      case 'Number':
        return (
          <TextField
            fullWidth
            key={column.name}
            label={column.name.replace(/_/g, ' ')}
            type="number"
            value={value}
            onChange={(e) => handleFilterChange(column.name, e.target.value)}
          />
        );

      case 'Date':
        {
          const range = value && typeof value === 'object' && !Array.isArray(value)
            ? value
            : { gte: '', lte: '' };
        return (
          <Box key={column.name}>
            <Typography variant="caption" color="text.secondary">
              {column.name.replace(/_/g, ' ')}
            </Typography>
            <Stack spacing={1} sx={{ mt: 0.5 }}>
              <AppDatePicker
                fullWidth
                size="medium"
                label="From"
                value={range.gte}
                onChange={(nextDate) => handleFilterChange(column.name, { ...range, gte: nextDate })}
              />
              <AppDatePicker
                fullWidth
                size="medium"
                label="To"
                value={range.lte}
                onChange={(nextDate) => handleFilterChange(column.name, { ...range, lte: nextDate })}
              />
            </Stack>
          </Box>
        );
        }

      case 'Yes/No':
        return (
          <FormControl fullWidth key={column.name}>
            <InputLabel>{column.name.replace(/_/g, ' ')}</InputLabel>
            <Select
              value={value === true ? 'true' : value === false ? 'false' : ''}
              label={column.name.replace(/_/g, ' ')}
              onChange={(e) => {
                if (e.target.value === '') {
                  handleFilterChange(column.name, '');
                  return;
                }
                handleFilterChange(column.name, e.target.value === 'true');
              }}
            >
              <MenuItem value="">
                <em>Any</em>
              </MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
        );

      case 'Text':
      default:
        return (
          <TextField
            fullWidth
            key={column.name}
            label={column.name.replace(/_/g, ' ')}
            value={Array.isArray(value) ? '' : value}
            onChange={(e) => handleFilterChange(column.name, e.target.value)}
          />
        );
    }
  };

  if (!tableSchema) {
    return null;
  }

  return (
    <Box 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <DateDraftScope ref={dateScopeRef} onPartialChange={handleDateDraftChange}>
      <form 
        onSubmit={handleSubmit}
        style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100%' 
        }}
      >
        {/* Scrollable area for filters */}
        <Box 
          sx={{ 
            overflowY: 'auto',
            flexGrow: 1,
            pr: 1, // Add padding right to prevent content from touching the scrollbar
            pb: 2, // Add padding bottom for better spacing
            // Add a subtle fade to bottom to indicate scrollable content
            maskImage: 'linear-gradient(to bottom, black 90%, transparent 100%)',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '3px',
            },
          }}
        >
          <Stack spacing={2} key={clearGeneration}>
            {/* Reporting-period filters */}
            {isTimeSeries && (
              <>
                <Typography variant="subtitle2">Reporting Period</Typography>
                <AppDatePicker
                  fullWidth
                  size="medium"
                  label="From"
                  value={startDate}
                  onChange={setStartDate}
                />
                <AppDatePicker
                  fullWidth
                  size="medium"
                  label="To"
                  value={endDate}
                  onChange={setEndDate}
                />
              </>
            )}

            {/* Metadata Filters */}
            {attributeColumns.length > 0 && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2">Attributes</Typography>
                  <IconButton size="small" onClick={() => setAttributeExpanded(!attributeExpanded)}>
                    {attributeExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                <Collapse in={attributeExpanded}>
                  <Stack spacing={2}>
                    {attributeColumns.map(renderMetadataFilter)}
                  </Stack>
                </Collapse>
              </>
            )}

            {/* Attribute Filters */}
            {detailColumns.length > 0 && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2">{isRaw ? 'Columns' : 'Details'}</Typography>
                  <IconButton size="small" onClick={() => setDetailExpanded(!detailExpanded)}>
                    {detailExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                <Collapse in={detailExpanded}>
                  <Stack spacing={2}>
                    {detailColumns.map(renderAttributeFilter)}
                  </Stack>
                </Collapse>
              </>
            )}
            
            {/* Add some space at the bottom to ensure last items aren't hidden under the button */}
            <Box sx={{ height: '16px' }} />
          </Stack>
        </Box>

        {/* Fixed button at the bottom */}
        <Box 
          sx={{ 
            pt: 2, 
            mt: 'auto', 
            position: 'sticky',
            bottom: 0,
            backgroundColor: (theme) => theme.palette.background.paper,
            zIndex: 1,
            borderTop: '1px solid rgba(255, 255, 255, 0.05)',
          }}
        >
          <Stack direction="row" spacing={1}>
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
              sx={{ flexShrink: 0 }}
            >
              Clear
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              fullWidth
            >
              {loading ? 'Loading...' : 'Apply Filters'}
            </Button>
          </Stack>

          {dateDraftError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {dateDraftError}
            </Typography>
          )}

          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
        </Box>
      </form>
      </DateDraftScope>
    </Box>
  );
}

export default DynamicFilters;

function IndexedColumnFilter({ column, isIdentifier, tableId, values, onChange, companyId }) {
  const [inputValue, setInputValue] = useState('');
  const { search, options, loading } = useIndexedValueSearch(tableId, column.name, { isIdentifier });

  // Always called: an emptied input clears the options and invalidates a read
  // still in flight, and a new search callback (table or column change)
  // re-runs the current term against the new scope.
  useEffect(() => {
    search(inputValue);
  }, [inputValue, search]);

  const optionLabels = (options || []).map(o => o?.value).filter(v => typeof v === 'string');
  const combinedOptions = Array.from(new Set([...(values || []), ...optionLabels]));

  // The search matches the typed text on its canonical letters and digits
  // (hooks/useIndexedValueSearch.js): an identifier whose canonical text starts
  // with the typed text's ("POL-123" offers POL123), and a spelling whose
  // canonical text contains it ("acme ltd" offers ACME-LTD). The picker's own
  // filter, looking for the typed text itself, would hide those matches, so the
  // options are kept on the search's own comparison instead.
  const keepSearchMatches = (searchOptions, { inputValue: typed }) => {
    const canonicalTyped = canonicalSearchText(typed);
    return searchOptions.filter((option) => (isIdentifier
      ? canonicalSearchText(option).startsWith(canonicalTyped)
      : canonicalSearchText(option).includes(canonicalTyped)));
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      options={combinedOptions}
      filterOptions={keepSearchMatches}
      value={values || []}
      renderTags={(selected, getTagProps) =>
        (selected || []).map((option, index) => {
          const label = typeof option === 'string' ? option : option?.value;
          // The tag's props carry its `key`, which React takes only on its own.
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Tooltip key={`${label}-${index}`} title={label} arrow>
              <span>
                <Chip key={key} label={label} {...tagProps} />
              </span>
            </Tooltip>
          );
        })
      }
      onChange={(_, newVals) => {
        const cleaned = (newVals || []).map(v => (typeof v === 'string' ? v : v?.value)).filter(v => typeof v === 'string' && v.trim() !== '');
        onChange(cleaned);
      }}
      inputValue={inputValue}
      onInputChange={(_, newInput) => setInputValue(newInput)}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={column.name.replace(/_/g, ' ')}
          placeholder={loading ? 'Searching...' : ''}
        />
      )}
    />
  );
}
